import { default as React } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import moment from 'moment';
import { t } from 'i18next';
import { withTranslation } from 'react-i18next';
import i18next from 'i18next';
import * as Score from "utils/Score";
import { ratingBuckets, positivePercent, MONO } from "./ReportViz";
import { api_url } from 'redux/config.js';

const LANGS = [
    { v: 'el', label: '🇬🇷 Ελληνικά' },
    { v: 'en', label: '🇬🇧 English' },
    { v: 'de', label: '🇩🇪 Deutsch' },
    { v: 'fr', label: '🇫🇷 Français' },
    { v: 'es', label: '🇪🇸 Español' },
    { v: 'pt', label: '🇵🇹 Português' },
];

// Report type ordinal for the AI tab (kept in sync with ViewReportPage.report_types).
class AISummary extends React.Component {
    constructor(props) {
        super(props);
        const uiLang = (i18next.language || 'el').slice(0, 2);
        this.state = {
            lang: LANGS.some(l => l.v === uiLang) ? uiLang : 'el',
            loading: false,
            summary: null,
            cached: false,
            error: null,
            quota: null, // { next_available_at }
            history: [],      // saved summaries of this survey (every generation is kept)
            savedInfo: null,  // { created_at, period_from, period_to } when viewing a saved one
        };
    }

    componentDidMount() {
        if (this.props.onRef) this.props.onRef(this);
        this.loadHistory();
    }

    aiPost = (data) => axios({
        url: api_url + 'survey/ai_summary',
        method: 'post',
        headers: { authorization: 'Bearer '.concat(this.props.user.token) },
        data: { survey_id: this.props.survey_id, ...data },
    });

    loadHistory = () => {
        this.aiPost({ history: true }).then(resp => {
            if (resp.data && resp.data.success) this.setState({ history: resp.data.history || [] });
        }).catch(() => { /* the list is a convenience — its failure must not block generation */ });
    }

    openSaved = (id) => {
        this.setState({ loading: true, error: null, quota: null });
        this.aiPost({ open_id: id }).then(resp => {
            if (resp.data && resp.data.success) {
                this.setState({
                    loading: false, summary: resp.data.summary, cached: false,
                    savedInfo: { created_at: resp.data.created_at, period_from: resp.data.period_from, period_to: resp.data.period_to },
                });
            } else {
                this.setState({ loading: false, error: (resp.data && resp.data.error) || 'Error' });
            }
        }).catch(err => {
            const r = err.response;
            this.setState({ loading: false, error: (r && r.data && r.data.error) || t('AI error') });
        });
    }

    // Export hooks are not applicable to the AI summary — declared so the report container's
    // PDF/Excel/CSV buttons no-op gracefully instead of throwing.
    generatePdf() { }
    generateCSV() { }

    sectionScale = (section, responses) => {
        let sc = 5;
        section.questions.forEach(q => {
            if (q.question.type === 'radio') {
                const rep = Score.getReportOfQuestion(q, responses);
                const mv = ratingBuckets(q, rep.reports).maxV;
                if (mv > sc) sc = mv;
            }
        });
        return sc;
    }

    // Section-average scores for a set of responses, keyed by section title (for the trend line).
    sectionScores = (surveyQuestion, responses) => {
        const out = {};
        surveyQuestion.sections.forEach(section => {
            let sum = 0, count = 0;
            section.questions.forEach(q => {
                if (q.question.type !== 'radio') return;
                const rep = Score.getReportOfQuestion(q, responses);
                if (rep.questionScore > 0) { sum += rep.questionScore; count++; }
            });
            if (count) out[section.sectionTitle] = Number((sum / count).toFixed(2));
        });
        return out;
    }

    // Build the grounding payload: the same aggregates the report shows, plus email-scrubbed,
    // location/name-free guest comments (score + text only).
    buildPayload = () => {
        const { surveyQuestion, surveyResponses, surveyResponses2, surveyMain } = this.props;
        const bScores = (surveyResponses2 && surveyResponses2.length) ? this.sectionScores(surveyQuestion, surveyResponses2) : null;

        let overallSum = 0, overallCount = 0, maxScale = 5, posSum = 0, posTotal = 0;
        const sections = [];
        surveyQuestion.sections.forEach(section => {
            const qs = [];
            let secSum = 0, secCount = 0;
            section.questions.forEach(q => {
                if (q.question.type !== 'radio') return;
                const rep = Score.getReportOfQuestion(q, surveyResponses);
                const b = ratingBuckets(q, rep.reports);
                if (b.maxV > maxScale) maxScale = b.maxV;
                if (rep.questionScore > 0) { secSum += rep.questionScore; secCount++; }
                if (b.total) {
                    const thr = Math.ceil((b.minV + b.maxV) / 2);
                    posSum += b.buckets.filter(x => x.value >= thr).reduce((a, x) => a + x.count, 0);
                    posTotal += b.total;
                }
                qs.push({
                    title: q.questionTitle,
                    score: Number((rep.questionScore || 0).toFixed(2)),
                    scale: b.maxV,
                    positive: positivePercent(q, rep.reports),
                    buckets: b.buckets.map(x => ({ label: x.label, count: x.count, pct: Math.round(x.pct) })),
                });
            });
            const secScore = secCount ? secSum / secCount : 0;
            if (secCount) { overallSum += secScore; overallCount++; }
            const sec = { title: section.sectionTitle, score: Number(secScore.toFixed(2)), scale: this.sectionScale(section, surveyResponses), questions: qs };
            if (bScores && bScores[section.sectionTitle] != null) sec.scorePreviousPeriod = bScores[section.sectionTitle];
            sections.push(sec);
        });

        const comments = [];
        surveyResponses.forEach(r => {
            const rscore = Score.getScoreOfOneResponse(surveyQuestion, r.answers);
            surveyQuestion.sections.forEach(section => section.questions.forEach(q => {
                if (q.question.type !== 'text') return;
                const ans = (r.answers || []).filter(a => a.id === q.id)[0];
                if (ans && ans.value && String(ans.value).trim()) {
                    comments.push({
                        score: Number((rscore || 0).toFixed(1)),
                        text: String(ans.value).replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, '[email]').slice(0, 500),
                    });
                }
            }));
        });

        return {
            surveyTitle: surveyMain ? surveyMain.title : '',
            overall: {
                score: Number((overallCount ? overallSum / overallCount : 0).toFixed(2)),
                scale: maxScale,
                responses: surveyResponses.length,
                positive: posTotal ? Math.round(posSum / posTotal * 100) : null,
                hasComparison: !!bScores,
            },
            sections,
            comments,
        };
    }

    generate = () => {
        const { filterData, survey_id } = this.props;
        const payload = this.buildPayload();
        this.setState({ loading: true, error: null, quota: null });
        const headers = { authorization: 'Bearer '.concat(this.props.user.token) };
        axios({
            url: api_url + 'survey/ai_summary',
            method: 'post',
            headers,
            data: {
                survey_id,
                from: filterData ? filterData.from1 : null,
                to: filterData ? filterData.to1 : null,
                lang: this.state.lang,
                payload,
            },
        }).then(resp => {
            if (resp.data && resp.data.success) {
                this.setState({ loading: false, summary: resp.data.summary, cached: !!resp.data.cached, savedInfo: null });
                this.loadHistory();
            } else {
                this.setState({ loading: false, error: (resp.data && resp.data.error) || 'Error' });
            }
        }).catch(err => {
            const r = err.response;
            if (r && r.status === 429) {
                this.setState({ loading: false, quota: { next_available_at: r.data && r.data.next_available_at } });
            } else if (r && r.status === 503) {
                this.setState({ loading: false, error: t('AI is not configured yet.') });
            } else {
                this.setState({ loading: false, error: (r && r.data && r.data.error) || t('AI error') });
            }
        });
    }

    renderResult() {
        const s = this.state.summary;
        if (!s) return null;
        const H4 = ({ children }) => <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '.06em', color: '#8a988f', fontWeight: 800, margin: '20px 0 8px' }}>{children}</div>;
        const list = (items, good) => (
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 6px' }}>
                {(items || []).map((it, i) => (
                    <li key={i} style={{ position: 'relative', padding: '7px 0 7px 26px', fontSize: 14.5, lineHeight: 1.55, borderBottom: '1px solid #eef2f0' }}>
                        <span style={{ position: 'absolute', left: 2, top: 7, color: good ? '#2f9e44' : '#d1495b' }}>{good ? '▲' : '▼'}</span>{it}
                    </li>
                ))}
            </ul>
        );
        return (
            <div>
                {this.state.cached && <div style={{ fontSize: 12, color: '#8a988f', marginBottom: 10 }}>
                    ↺ {t('Shown from your earlier summary for this period (no new generation used).')}
                </div>}
                {this.state.savedInfo && <div style={{ fontSize: 12, color: '#8a988f', marginBottom: 10 }}>
                    🗂 {t('Saved summary from')} {moment(this.state.savedInfo.created_at).format('LL')}
                    {this.state.savedInfo.period_from ? ` — ${t('Period')}: ${moment(Number(this.state.savedInfo.period_from)).format('L')} – ${moment(Number(this.state.savedInfo.period_to)).format('L')}` : ''}
                </div>}
                {s.overview && <p style={{ fontSize: 15.5, lineHeight: 1.6, margin: '4px 0 10px' }}>{s.overview}</p>}
                {s.working && s.working.length > 0 && <div><H4>{t('Working well')}</H4>{list(s.working, true)}</div>}
                {s.attention && s.attention.length > 0 && <div><H4>{t('Needs attention')}</H4>{list(s.attention, false)}</div>}
                {s.trend && <div><H4>{t('Trend vs previous period')}</H4><p style={{ fontSize: 14.5, lineHeight: 1.6 }}>{s.trend}</p></div>}
                {s.themes && s.themes.length > 0 && <div>
                    <H4>{t('What guests are writing about')}</H4>
                    {s.themes.map((th, i) => (
                        <blockquote key={i} style={{ margin: '8px 0', padding: '9px 14px', borderLeft: '3px solid #cfd8d3', background: '#f6f9f7', borderRadius: '0 8px 8px 0', fontSize: 13.5, color: '#5d6d66', fontStyle: 'italic' }}>
                            “{th.quote}”{th.source ? <span style={{ display: 'block', fontStyle: 'normal', fontSize: 11.5, color: '#8a988f', marginTop: 4 }}>{th.source}</span> : null}
                        </blockquote>
                    ))}
                </div>}
                {s.actions && s.actions.length > 0 && <div>
                    <H4>{t('Recommended actions')}</H4>
                    <ol style={{ margin: 0, paddingLeft: 20 }}>
                        {s.actions.map((a, i) => <li key={i} style={{ fontSize: 14.5, lineHeight: 1.55, marginBottom: 7 }}>{a}</li>)}
                    </ol>
                </div>}
                <div style={{ marginTop: 20, fontSize: 11.5, color: '#8a988f', borderTop: '1px solid #e2e8e4', paddingTop: 12, lineHeight: 1.5 }}>
                    ✨ {t('Generated from aggregated ratings and anonymised comments for the selected period. Guest names and emails are redacted before analysis. AI summaries can contain mistakes — treat as a starting point, not a substitute for the underlying data.')}
                </div>
            </div>
        );
    }

    render() {
        const { loading, quota, error } = this.state;
        const cardStyle = { background: '#fff', border: '1px solid #e2e8e4', borderRadius: 14, boxShadow: '0 1px 2px rgba(20,40,30,.06),0 6px 20px rgba(20,40,30,.05)', overflow: 'hidden' };
        const nextTxt = quota && quota.next_available_at ? moment(quota.next_available_at).fromNow() : null;
        return (
            <div style={cardStyle}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8e4', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>✨ {t('AI summary')}
                        <span style={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em', color: '#8a988f', border: '1px solid #cfd8d3', borderRadius: 99, padding: '2px 8px', marginLeft: 8 }}>3 / 24h</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <select value={this.state.lang} onChange={e => this.setState({ lang: e.target.value })}
                            style={{ fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: '#1b2320', background: '#fff', border: '1px solid #cfd8d3', borderRadius: 9, padding: '8px 10px', cursor: 'pointer' }}>
                            {LANGS.map(l => <option key={l.v} value={l.v}>{l.label}</option>)}
                        </select>
                        <button onClick={this.generate} disabled={loading}
                            style={{ fontFamily: 'inherit', fontSize: 13, fontWeight: 700, color: '#fff', background: 'linear-gradient(135deg,#2f9e44,#3b6fd4)', border: 0, borderRadius: 9, padding: '9px 16px', cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.65 : 1 }}>
                            {loading ? t('Analysing…') : (this.state.summary ? '↻ ' + t('Regenerate') : t('Generate summary'))}
                        </button>
                    </div>
                </div>
                <div style={{ padding: '16px 18px' }}>
                    {!this.state.summary && !loading && !quota && !error && (
                        <div style={{ color: '#5d6d66', fontSize: 14 }}>
                            {t('Pick a language, then generate a plain-language overview of the selected period — highlights, what needs attention, trend vs the previous period, and the themes guests raise. Grounded in the responses shown; guest names & emails are redacted before analysis.')}
                            <div style={{ marginTop: 10, fontSize: 12.5, color: '#8a988f' }}>ℹ️ {t('Included in your plan: 3 AI summaries per 24 hours.')}</div>
                        </div>
                    )}
                    {loading && <div style={{ color: '#5d6d66', fontSize: 14 }}>{t('Reading the responses and their comments…')}</div>}
                    {quota && <div style={{ color: '#5d6d66', fontSize: 14 }}>🔒 {t('You have used all your AI summaries for today.')} {nextTxt ? t('Available again') + ' ' + nextTxt + '.' : ''}</div>}
                    {error && <div style={{ color: '#d1495b', fontSize: 14 }}>⚠️ {error}</div>}
                    {!loading && this.renderResult()}
                    {this.renderHistory()}
                </div>
            </div>
        );
    }

    // Every generated summary is kept; this lists them so an earlier one can be reopened at any
    // time — e.g. last month's summary after the period filter has moved on.
    renderHistory() {
        const { history, loading } = this.state;
        if (!history || history.length === 0) return null;
        const langFlag = (v) => { const l = LANGS.filter(x => x.v === v)[0]; return l ? l.label : v; };
        return (
            <div style={{ marginTop: 22, borderTop: '1px solid #e2e8e4', paddingTop: 14 }}>
                <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '.06em', color: '#8a988f', fontWeight: 800, marginBottom: 4 }}>
                    🗂 {t('Saved summaries')} ({history.length})
                </div>
                <div style={{ fontSize: 12, color: '#8a988f', marginBottom: 8 }}>{t('Every summary you generate is saved here automatically.')}</div>
                {history.map(h => (
                    <div key={h.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '8px 0', borderBottom: '1px solid #eef2f0', flexWrap: 'wrap' }}>
                        <div style={{ fontSize: 13.5 }}>
                            <span style={{ fontWeight: 600 }}>{moment(h.created_at).format('LL')}</span>
                            <span style={{ color: '#8a988f', marginLeft: 8 }}>{langFlag(h.lang)}</span>
                            {h.period_from ? <span style={{ color: '#8a988f', marginLeft: 8, fontFamily: MONO, fontSize: 12 }}>
                                {moment(Number(h.period_from)).format('L')} – {moment(Number(h.period_to)).format('L')}
                            </span> : null}
                        </div>
                        <button onClick={() => this.openSaved(h.id)} disabled={loading}
                            style={{ fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, color: '#2f6b4f', background: '#eef7f1', border: '1px solid #cfe6d7', borderRadius: 8, padding: '6px 14px', cursor: 'pointer' }}>
                            {t('Open')}
                        </button>
                    </div>
                ))}
            </div>
        );
    }
}

const mapStateToProps = ({ user }) => ({ user });
export default connect(mapStateToProps)(withTranslation()(AISummary));
